import React, { useEffect, useMemo, useState } from 'react';
import './Analytics.css';
import { getBnbHostAnalytics, getBnbHostRentAnalytics } from '../../../api/services/analyticsService';

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const currency = (value) => {
  const num = safeNumber(value);
  return `KES ${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const Analytics = () => {
  const now = new Date();
  const [snapshot, setSnapshot] = useState(null);
  const [rentAnalytics, setRentAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRent, setLoadingRent] = useState(false);
  const [error, setError] = useState(null);

  // Revenue period selectors
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-indexed
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Re-fetch whenever month/year changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await getBnbHostAnalytics({ month: selectedMonth, year: selectedYear });
        if (!mounted) return;
        setSnapshot(resp?.data || null);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load BNB host analytics');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedMonth, selectedYear]);

  const snap = snapshot || {};
  const rent = rentAnalytics || {};

  const availableYears = useMemo(() => {
    const current = now.getFullYear();
    return Array.from({ length: 6 }, (_, i) => current - i);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const periodLabel = `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`;

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingRent(true);
      try {
        const resp = await getBnbHostRentAnalytics({ year: selectedYear, month: selectedMonth });
        if (!mounted) return;
        setRentAnalytics(resp?.data || null);
      } catch (e) {
        if (!mounted) return;
        setRentAnalytics(null);
      } finally {
        if (!mounted) return;
        setLoadingRent(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedMonth, selectedYear]);

  // Bookings totals
  const totalBookings =
    safeNumber(snap.bookingsPendingCount) +
    safeNumber(snap.bookingsConfirmedCount) +
    safeNumber(snap.bookingsCancelledCount) +
    safeNumber(snap.bookingsRejectedCount) +
    safeNumber(snap.bookingsCompletedCount);

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div>
          <h1 className="page-title">BnB Analytics</h1>
          <p className="page-subtitle">Your short-stay property performance at a glance</p>
        </div>
      </div>

      {error ? (
        <div className="analytics-alert analytics-alert-error">
          <div className="analytics-alert-title">Couldn't load analytics</div>
          <div className="analytics-alert-message">{error}</div>
        </div>
      ) : null}

      {/* Property & Unit Snapshot */}
      <div className="metrics-row">
        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">🏢</div>
          <div className="metric-content">
            <div className="metric-label">Properties</div>
            <div className="metric-value">{safeNumber(snap.propertiesCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Total properties</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">🏠</div>
          <div className="metric-content">
            <div className="metric-label">Units</div>
            <div className="metric-value">{safeNumber(snap.unitsCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Total units</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-check">🧑‍💼</div>
          <div className="metric-content">
            <div className="metric-label">Agents Assigned</div>
            <div className="metric-value">{safeNumber(snap.agentsAssignedCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Assigned agents</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-dollar">📅</div>
          <div className="metric-content">
            <div className="metric-label">Total Bookings</div>
            <div className="metric-value">{totalBookings}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">All statuses</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bookings Status + Revenue */}
      <div className="charts-row">
        {/* Bookings Status */}
        <div className="chart-card">
          <h3 className="chart-title">Bookings Status</h3>
          <p className="chart-subtitle">Overview of all booking statuses</p>
          <div className="analytics-mini-grid">
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">⏳ Pending</div>
              <div className="analytics-mini-value">{safeNumber(snap.bookingsPendingCount)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">✅ Confirmed</div>
              <div className="analytics-mini-value">{safeNumber(snap.bookingsConfirmedCount)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">🏁 Completed</div>
              <div className="analytics-mini-value">{safeNumber(snap.bookingsCompletedCount)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">❌ Cancelled</div>
              <div className="analytics-mini-value">{safeNumber(snap.bookingsCancelledCount)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">🚫 Rejected</div>
              <div className="analytics-mini-value">{safeNumber(snap.bookingsRejectedCount)}</div>
            </div>
          </div>
        </div>

        {/* Revenue – with period selectors */}
        <div className="chart-card">
          <div className="analytics-section-header" style={{ marginBottom: '4px' }}>
            <div>
              <h3 className="chart-title">Revenue</h3>
              <p className="chart-subtitle">Paid and pending revenue for {periodLabel}</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <select
                className="month-selector period-selector"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                style={{ background: '#fff', color: '#1a1a2e', border: '1px solid #ddd' }}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={idx + 1} value={idx + 1}>{name}</option>
                ))}
              </select>
              <select
                className="year-selector period-selector"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                style={{ background: '#fff', color: '#1a1a2e', border: '1px solid #ddd' }}
              >
                {availableYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="analytics-mini-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">💰 Paid</div>
              <div className="analytics-mini-value">{currency(rent.paid)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">⏳ Pending</div>
              <div className="analytics-mini-value">{currency(rent.pending)}</div>
            </div>
          </div>
          <div className="analytics-muted">
            {loadingRent ? 'Loading revenue stats…' : (rent.byMonth ? 'Monthly breakdown available.' : 'No monthly breakdown available.')}
          </div>
        </div>
      </div>

      {/* Maintenance Snapshot */}
      <div className="charts-row">
        <div className="chart-card full-width">
          <h3 className="chart-title">Maintenance Snapshot</h3>
          <p className="chart-subtitle">Current maintenance request statuses across your properties</p>
          <div className="analytics-maintenance-grid">
            <div className="analytics-maintenance-item">
              <span className="analytics-maintenance-label">🔓 Open</span>
              <span className="analytics-maintenance-value">{safeNumber(snap.maintenanceOpenCount)}</span>
            </div>
            <div className="analytics-maintenance-item">
              <span className="analytics-maintenance-label">🔧 Handled</span>
              <span className="analytics-maintenance-value">{safeNumber(snap.maintenanceHandledCount)}</span>
            </div>
            <div className="analytics-maintenance-item">
              <span className="analytics-maintenance-label">✅ Completed</span>
              <span className="analytics-maintenance-value">{safeNumber(snap.maintenanceCompletedCount)}</span>
            </div>
            <div className="analytics-maintenance-item">
              <span className="analytics-maintenance-label">❌ Cancelled</span>
              <span className="analytics-maintenance-value">{safeNumber(snap.maintenanceCancelledCount)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
